import gsap from 'gsap'
import './Footer.css'
import { splitReveal, splitConceal, SplitFx } from '../../utils/text.js'
import { UnderlineFx, attachHoverUnderline } from '../../utils/underline.js'
import { STAGGER } from '../../../shared/motion.js'

// 卡片朝向模式，對應 Carousel.setMode；預設選第一個（與 Carousel 預設一致）
const MODES = [
    { id: 'curve', label: 'CURVE' },
    { id: 'straight', label: 'STRAIGHT' }
]

const LABELS = {
    description: 'INDEPENDENT DEVELOPER'
}

/**
 * 單顆 Mode 按鈕的行為封裝單元
 * 包辦 DOM (el / label) 與底線控制，對外僅暴露動畫與底線繪製介面
 */
class ModeButton {
    constructor(el) {
        this.el = el
        this.id = el.dataset.mode
        this.label = el.querySelector('.mode__label')
        this.underline = new UnderlineFx(el)
    }

    drawIn() {
        return this.underline.drawIn()
    }

    erase() {
        return this.underline.erase()
    }

    showUnderline() {
        return this.underline.show()
    }

    resetUnderline() {
        return this.underline.reset()
    }
}

class AppFooter extends HTMLElement {
    constructor() {
        super()
        this.activeUnit = null
        this.modeUnits = []
        this.modeLabels = []
        this._director = null

        // 動畫與 Split 實例快取
        this._modesSplit = null
        this.descriptionFx = null
    }

    setDirector(director) {
        this._director = director
    }

    connectedCallback() {
        this._renderDOM()
        this._setupDOMReferences()
        this._setupEvents()
        this._initDefaultState()
    }

    // ─────────────────────────────────────────────────────────────
    // DOM & Initialization Helpers
    // ─────────────────────────────────────────────────────────────

    _renderDOM() {
        this.innerHTML = /* html */ `
            <div class="footer">
                <div class="footer__left">
                    <div class="modes">
                        ${MODES.map((m) => {
                            return `
                            <button class="mode" data-mode="${m.id}">
                                <span class="mode__label">${m.label}</span>
                            </button>
                        `
                        }).join('')}
                    </div>
                </div>
                <button class="description">
                    <span class="description__label">${LABELS.description}</span>
                </button>
            </div>
        `
    }

    _setupDOMReferences() {
        this.modeUnits = [...this.querySelectorAll('.mode')].map((el) => {
            return new ModeButton(el)
        })
        this.modeLabels = this.modeUnits.map((unit) => {
            return unit.label
        })

        // 右側描述字樣（INDEPENDENT DEVELOPER）
        this.descriptionEl = this.querySelector('.description')
        this.descriptionFx = new SplitFx(this.querySelector('.description__label'))
    }

    _setupEvents() {
        attachHoverUnderline(this.descriptionEl)

        this.modeUnits.forEach((unit) => {
            unit.el.addEventListener('mouseenter', () => {
                return this._previewIn(unit)
            })
            unit.el.addEventListener('mouseleave', () => {
                return this._previewOut(unit)
            })
            unit.el.addEventListener('click', () => {
                return this._selectMode(unit)
            })
        })
    }

    _initDefaultState() {
        // 預設選 CURVE（與 Carousel 預設一致）；找不到才退回第一顆
        this.activeUnit =
            this.modeUnits.find((unit) => {
                return unit.id === 'curve'
            }) ?? this.modeUnits[0]
        this.activeUnit.showUnderline()
    }

    // ─────────────────────────────────────────────────────────────
    // State & Interactive Handlers
    // ─────────────────────────────────────────────────────────────

    /** 檔掉隱形狀態下的 Hover / Click 事件 */
    _setInteractive(active) {
        const value = active ? 'all' : 'none'
        this.modeUnits.forEach((unit) => {
            unit.el.style.pointerEvents = value
        })
        this.descriptionEl.style.pointerEvents = value
    }

    _previewIn(unit) {
        if (unit !== this.activeUnit) {
            unit.drawIn()
        }
    }

    _previewOut(unit) {
        if (unit !== this.activeUnit) {
            unit.erase()
        }
    }

    _selectMode(unit) {
        if (unit === this.activeUnit) {
            return
        }

        const prevUnit = this.activeUnit
        this.activeUnit = unit

        unit.drawIn()
        prevUnit.erase()

        this._director?.setCarouselMode?.(unit.id)
    }

    _cleanupAnimations() {
        this._modesSplit?.revert()
        this._modesSplit = null
    }

    // ─────────────────────────────────────────────────────────────
    // Motion Transitions
    // ─────────────────────────────────────────────────────────────

    /** Mode 按鈕 split 退場（往下消失）+ 收起 active 底線 */
    modesOut() {
        this._cleanupAnimations()

        const { split, tween } = splitConceal(this.modeLabels, { y: '100%', stagger: STAGGER.none })
        this._modesSplit = split

        this.modeUnits.forEach((unit) => {
            unit === this.activeUnit ? unit.erase() : unit.resetUnderline()
        })

        const tl = gsap.timeline()
        tl.add(tween, 0)
        return tl
    }

    /** Mode 按鈕 split 進場 + 還原 active 底線 */
    modesIn() {
        this._cleanupAnimations()

        const { split, tween } = splitReveal(this.modeLabels, { y: '100%' })
        this._modesSplit = split

        tween.eventCallback('onStart', () => {
            this.activeUnit.showUnderline()
        })

        const tl = gsap.timeline()
        tl.add(tween, 0)
        return tl
    }

    /** Home Chrome 進場：Mode 按鈕與右側描述字樣同步 split 進 */
    show() {
        this.style.visibility = 'visible'
        this._setInteractive(true)

        const tl = gsap.timeline()
        tl.add(this.modesIn(), 0)

        const descriptionTween = this.descriptionFx.reveal(LABELS.description, { y: '100%' })
        if (descriptionTween) {
            tl.add(descriptionTween, 0)
        }

        return tl
    }

    /** Home Chrome 離場：Mode 按鈕與右側描述字樣同步 split 出 */
    hide() {
        this._setInteractive(false)

        const tl = gsap.timeline()
        tl.add(this.modesOut(), 0)

        const descriptionOut = this.descriptionFx.conceal({ y: '100%', stagger: STAGGER.none })
        if (descriptionOut) {
            tl.add(descriptionOut, 0)
        }

        return tl
    }

    disconnectedCallback() {
        this._cleanupAnimations()
        this.descriptionFx?.clear()
    }
}

if (!customElements.get('app-footer')) {
    customElements.define('app-footer', AppFooter)
}
